import useScrollUp from '@susu/custom-made/hooks/useScrollUp';
import { ThemeContext } from '@susu/custom-made/providers';
import { Clients } from '@susu/custom-made/utils/constants';
import type { Monogram, MonogramColor, Option } from '@susu/custom-made/utils/interfaces';
import cn from 'classnames';
import type { Dispatch, SetStateAction } from 'react';
import { useContext, useState } from 'react';

import Button, { ButtonType } from '../../button/button';
import type { typeCard } from '../../card/card';
import { CardOrientation } from '../../card/card';
import CardList, { CardListType } from '../../cardList/cardList';
import Panel, { PanelSize } from '../../panel/panel';
import TextInput, { TextBorderType } from '../../textInput/textInput';
import styles from './monogramDetailsPanel.module.scss';

export enum Step {
  Initials = 'initials',
  Color = 'color',
  FontStyle = 'fontStyle',
}

export const MonogramDetailsPanel = ({
  monogramData,
  monogramObj,
  selectedMonogramId,
  isMonogramDetailsShown,
  setIsMonogramDetailsShown,
  setMonogramObj,
  applyMonogram,
}: {
  monogramData: Option | undefined;
  monogramObj: Monogram;
  selectedMonogramId: number;
  isMonogramDetailsShown: boolean;
  setIsMonogramDetailsShown: Dispatch<SetStateAction<boolean>>;
  setMonogramObj: Dispatch<SetStateAction<Monogram>>;
  applyMonogram: () => void;
}) => {
  const [isError, setIsError] = useState<boolean>(false);
  const [step, setStep] = useState<Step>(Step.Initials);
  const { state } = useContext(ThemeContext);
  const scrollUpColor = useScrollUp();
  const scrollUpFont = useScrollUp();

  const changeMonogramInitials = (text: string) => {
    const shirtFormat = /^[a-zA-Z0-9,./ ]+$/;
    const allFormat = /^[a-zA-Z0-9,./'& üûùúîíìïöôòóœøõáàâäãåæçèéêëÿźñ]+$/;
    const format = state.product === 'Shirt' || state.product === 'TuxedoShirt' ? shirtFormat : allFormat;
    const formatSpaces = /^\s+$/;
    if ((format.test(text) && !formatSpaces.test(text)) || !text.length) {
      setIsError(false);
    } else {
      setIsError(true);
    }

    const tmpMonogramObj = { ...monogramObj };
    if (tmpMonogramObj[selectedMonogramId]?.initials) {
      tmpMonogramObj[selectedMonogramId].initials = text;
    } else {
      tmpMonogramObj[selectedMonogramId] = {
        ...tmpMonogramObj[selectedMonogramId],
        initials: text,
      };
    }
    setMonogramObj(tmpMonogramObj);
  };

  const closeMonogramDetails = () => {
    setIsMonogramDetailsShown(false);
    setMonogramObj({});
    setStep(Step.Initials);
    setIsError(false);
  };

  const changeNextStep = () => {
    if (step === Step.Initials) {
      setStep(Step.Color);
    } else if (step === Step.Color) {
      setStep(Step.FontStyle);
    } else if (step === Step.FontStyle) {
      applyMonogram();
      closeMonogramDetails();
    }
  };

  const changePreviousStep = () => {
    if (step === Step.Initials) {
      closeMonogramDetails();
    } else if (step === Step.Color) {
      setStep(Step.Initials);
    } else if (step === Step.FontStyle) {
      setStep(Step.Color);
    }
  };

  const checkDisabled = () => {
    if (step === Step.Initials) {
      return !monogramObj[selectedMonogramId ?? 0]?.initials?.length || isError;
    } else if (step === Step.Color) {
      return !monogramObj[selectedMonogramId ?? 0]?.colorId;
    } else if (step === Step.FontStyle) {
      return !monogramObj[selectedMonogramId ?? 0]?.fontStyleId;
    }
  };

  const colorClick = (card: typeCard) => {
    const tmpMonogramObj = { ...monogramObj };
    if (tmpMonogramObj[selectedMonogramId]?.colorId) {
      tmpMonogramObj[selectedMonogramId].colorId = card.id;
    } else {
      tmpMonogramObj[selectedMonogramId] = {
        ...tmpMonogramObj[selectedMonogramId],
        colorId: card.id,
      };
    }
    setMonogramObj(tmpMonogramObj);
  };

  const fontClick = (card: typeCard) => {
    const tmpMonogramObj = { ...monogramObj };
    if (tmpMonogramObj[selectedMonogramId]?.fontStyleId) {
      tmpMonogramObj[selectedMonogramId].fontStyleId = card.id;
    } else {
      tmpMonogramObj[selectedMonogramId] = {
        ...tmpMonogramObj[selectedMonogramId],
        fontStyleId: card.id,
      };
    }
    setMonogramObj(tmpMonogramObj);
  };

  return (
    <Panel isActive={isMonogramDetailsShown} closeClick={closeMonogramDetails} panelSize={PanelSize.Full} zIndex={22}>
      {isMonogramDetailsShown && (
        <div
          className={cn(styles.monogramContainer, {
            [styles.inStore]: state.client === Clients.inStore,
          })}
        >
          <div
            className={cn(styles.stepContainer, {
              [styles.stepActive]: step === Step.Initials,
              [styles.stepPrevious]: step === Step.Color || step === Step.FontStyle,
            })}
          >
            <div className={cn(styles.header, styles.show)} data-test-id="monogram-initials-header">
              Monogram Initials
            </div>
            <div className={styles.textContainer}>
              <TextInput
                onChange={changeMonogramInitials}
                placeholder="Enter your initials"
                textValue={monogramObj[selectedMonogramId]?.initials ?? ''}
                inputTestId="monogram-initials-txt"
                maxLength={monogramData?.initialsMaxLength}
                charCounter={`${
                  monogramObj[selectedMonogramId]?.initials?.length ?? 0
                }/${monogramData?.initialsMaxLength}`}
                textBorderType={TextBorderType.Square}
                errorMessage={
                  isError
                    ? 'Your initials can only contain letters, numbers, periods, commas, forward slashes and spaces.'
                    : undefined
                }
              />
            </div>
          </div>
          <div
            className={cn(styles.stepContainer, {
              [styles.stepActive]: step === Step.Color,
              [styles.stepNext]: step === Step.Initials,
              [styles.stepPrevious]: step === Step.FontStyle,
            })}
            onScroll={e => {
              scrollUpColor.handleScroll(e, 82);
            }}
          >
            <div
              className={cn(styles.header, {
                [styles.show]: scrollUpColor.scrollingUp,
              })}
              data-test-id="monogram--color-header"
            >
              Monogram Color
            </div>
            <div className={styles.cardListContainer}>
              <CardList
                cardItems={
                  monogramData?.colors
                    ? Object.values(monogramData?.colors).sort((a: MonogramColor, b: MonogramColor) => {
                        return a.sequenceNo - b.sequenceNo;
                      })
                    : []
                }
                selectedItemId={monogramObj[selectedMonogramId]?.colorId}
                cardClick={colorClick}
                cardOrientation={CardOrientation.Vertical}
                cardListType={CardListType.Monogram}
                testId="monogram-color-list"
                section={state.section}
              />
            </div>
          </div>
          <div
            className={cn(styles.stepContainer, {
              [styles.stepActive]: step === Step.FontStyle,
              [styles.stepNext]: step === Step.Color || step === Step.Initials,
            })}
            onScroll={e => {
              scrollUpFont.handleScroll(e, 82);
            }}
          >
            <div
              className={cn(styles.header, {
                [styles.show]: scrollUpFont.scrollingUp,
              })}
              data-test-id="monogram-fontStyle-header"
            >
              Monogram Font Style
            </div>
            <div className={styles.cardListContainer}>
              <CardList
                cardItems={monogramData?.fontTypes ? Object.values(monogramData?.fontTypes) : []}
                selectedItemId={monogramObj[selectedMonogramId]?.fontStyleId}
                cardClick={fontClick}
                cardOrientation={CardOrientation.Vertical}
                cardListType={CardListType.Monogram}
                testId="monogram-font-list"
                section={state.section}
              />
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              isFullSize={true}
              onClick={changePreviousStep}
              type={ButtonType.Secondary}
              testId="monogram-cancel-btn"
            >
              {step === Step.Initials ? 'Close' : 'Previous'}
            </Button>
            <Button
              isFullSize={true}
              onClick={changeNextStep}
              type={ButtonType.Primary}
              testId="monogram-next-btn"
              isDisabled={checkDisabled()}
            >
              {step === Step.FontStyle ? 'Apply' : 'Next'}
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
};
